import React, { useState, useEffect, useMemo } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { themeAtom, isAdminAtom, staffDataAtom } from "../../../../-Atoms";

// Firebase imports
import { db, onSnapshot, doc, updateDoc } from "../../../../firebaseConfig";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/en-au";
import dayjs from "dayjs";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LinkIcon from "@mui/icons-material/Link";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LaunchIcon from "@mui/icons-material/Launch";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionActions from "@mui/material/AccordionActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import TableRowsTwoToneIcon from "@mui/icons-material/TableRowsTwoTone";
import SettingsIcon from "@mui/icons-material/Settings";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

import UniversalTextField from "../../../../GlobalComponents/UniversalTextField";
import CameraSettings from "../../../EventDataModalV2/components/PreProdTab/CameraSettings";
import NotesContainer from "../../../EventDataModalV2/components/NotesContainer";
import BriefsContainer from "../../../EventDataModalV2/components/PreProdTab/BriefsContainer";
import SetupAccordionV2 from "./SetupAccordionV2";
import AttachmentsV2 from "../AttachmentsV2";
import SuppliedFoldersDialog from "./SuppliedFoldersDialog";

import { v4 as uuidv4 } from "uuid";

const ShootInfoTab = ({
  jobData,
  clientData,
  setHasUnsavedChanges,
  setUrlModalUrl,
  setURLModalOpen,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [staffData] = useAtom(staffDataAtom);

  // Date states
  const [dueDate, setDueDate] = useState(
    jobData.dueDate ? dayjs(jobData.dueDate) : null
  );
  const [expressDueDate, setExpressDueDate] = useState(
    jobData.expressDueDate ? dayjs(jobData.expressDueDate) : null
  );

  // Filter states
  const [selectedNotesFilter, setSelectedNotesFilter] = useState([]);

  // Menu states
  const [supplyLinkMenuAnchor, setSupplyLinkMenuAnchor] = useState(null);

  // Setup states
  const [setups, setSetups] = useState({});

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Client selects states
  const [selectsSetup, setSelectsSetup] = useState(false);

  // Supplied folders dialog state
  const [visibleFoldersDialogOpen, setVisibleFoldersDialogOpen] = useState(false);

  // Sync with jobData changes
  useEffect(() => {
    setDueDate(jobData.dueDate ? dayjs(jobData.dueDate) : null);
    setExpressDueDate(
      jobData.expressDueDate ? dayjs(jobData.expressDueDate) : null
    );
  }, [jobData]);

  // Sync with clientData changes
  useEffect(() => {
    setSetups(clientData?.setups || {});
  }, [clientData]);

  // Listen for client selects changes
  useEffect(() => {
    if (jobData.id) {
      let unsub = onSnapshot(doc(db, "ClientSelects", jobData.id), (doc) => {
        try {
          const exists = doc.exists();
          setSelectsSetup(exists);
        } catch (e) {
          console.error(e);
        }
      });
      return () => {
        unsub();
      };
    }
  }, [jobData]);

  // Helper function to update job data
  const updateJobData = (field, value) => {
    if (!jobData?.id) return;

    updateDoc(doc(db, "JobData", jobData.id), { [field]: value }).catch(
      (error) => {
        console.error(`Error updating ${field}:`, error);
      }
    );
  };

  // Helper function to update client selects data
  const updateSelectsData = (field, value) => {
    if (!jobData?.id) return;

    updateDoc(doc(db, "ClientSelects", jobData.id), { [field]: value }).catch(
      (error) => {
        console.error(`Error updating selects ${field}:`, error);
      }
    );
  };

  // Helper function to find staff name by ID
  const findStaffName = (staffId) => {
    if (!staffId) return "";

    // Handle external staff objects
    if (typeof staffId === "object" && staffId.type === "external") {
      return staffId.name || "External";
    }

    if (!staffData) return "";
    const staff = staffData.find((s) => s.id === staffId);
    return staff ? staff.preferredName || staff.firstName || "" : "";
  };

  // Date handlers
  const handleDueDateChange = (newValue) => {
    const dateValue = newValue ? dayjs(newValue) : null;
    setDueDate(dateValue);
    updateJobData("dueDate", dateValue ? dateValue.toISOString() : null);
  };

  const handleExpressDueDateChange = (newValue) => {
    const dateValue = newValue ? dayjs(newValue) : null;
    setExpressDueDate(dateValue);
    updateJobData("expressDueDate", dateValue ? dateValue.toISOString() : null);
  };

  const clearExpressDate = () => {
    setExpressDueDate(null);
    updateJobData("expressDueDate", null);
  };

  // Toggle handlers
  const handleSelectsLockedToggle = () => {
    const newValue = !jobData.selectsAreLocked;
    updateJobData("selectsAreLocked", newValue);
  };

  const handleClientReselectToggle = () => {
    updateSelectsData("selectsMade", false);
    updateSelectsData("imagesProcessed", false);
    // Note: selectsMadeTimestamp and archived selects remain for history
  };

  // Filter handlers
  const handleFilterChange = (event, newFilter) => {
    setSelectedNotesFilter(newFilter);
  };

  // Supply link handlers
  const handleSupplyLinkClick = (event) => {
    setSupplyLinkMenuAnchor(event.currentTarget);
  };

  const handleSupplyLinkMenuClose = () => {
    setSupplyLinkMenuAnchor(null);
  };

  const copySupplyLinkToClipboard = () => {
    if (jobData.supplyLink) {
      navigator.clipboard.writeText(jobData.supplyLink).then(() => {
        setSnackbarMessage("Supply portal link copied to clipboard");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      });
    }
    handleSupplyLinkMenuClose();
  };

  const openSupplyLink = () => {
    if (jobData.supplyLink) {
      window.open(jobData.supplyLink, "_blank");
    }
    handleSupplyLinkMenuClose();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
  };

  const filterButtonStyles = {
    fontSize: 11,
    fontWeight: "bold",
    padding: "6px 10px",
    minWidth: "auto",
    border: `1px solid ${theme === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"
      } !important`,
    borderRadius: "8px !important",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    textTransform: "none",
    boxShadow:
      theme === "dark"
        ? "0 2px 5px rgba(0,0,0,0.2)"
        : "0 2px 5px rgba(0,0,0,0.05)",
    whiteSpace: "nowrap",
  };

  // Determine if supply link exists
  const supplyLink = jobData?.supplyLink;
  const supplyPortalUrl = jobData?.id
    ? process.env.NODE_ENV === "production"
      ? `https://supply.imagesthatsell.com.au/${jobData.id}`
      : `https://localhost:3000/supply/${jobData.id}`
    : null;

  const studioCounts = useMemo(
    () => ({
      studio401: setups?.studio401?.length || 0,
      studio301: setups?.studio301?.length || 0,
      studio302: setups?.studio302?.length || 0,
      studio403: setups?.studio403?.length || 0,
    }),
    [setups]
  );

  const renderSetupChip = (count) => (
    <Chip
      label={`${count} setup${count === 1 ? "" : "s"}`}
      size="small"
      color={count > 0 ? "primary" : "default"}
      variant={count > 0 ? "filled" : "outlined"}
      sx={{
        height: 24,
        fontWeight: 600,
      }}
    />
  );

  // Handle setup creation
  const handleCreateSetup = (studioKey) => {
    const newSetups = JSON.parse(JSON.stringify(setups));
    if (!newSetups[studioKey]) newSetups[studioKey] = [];
    newSetups[studioKey].unshift({
      name: "",
      date: new Date().toISOString(),
      id: uuidv4(),
      eips: [],
      images: [],
      notes: "",
    });
    setSetups(newSetups);
    updateDoc(doc(db, "ClientDatabase", clientData.id), {
      setups: newSetups,
    });
  };

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
      >
        {/* Supply Portal Button */}
        <motion.div variants={itemVariants} style={{ display: "flex", gap: 8 }}>
          <Button
            variant="contained"
            startIcon={<LocalShippingIcon />}
            fullWidth
            component="a"
            href={supplyPortalUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!supplyPortalUrl}
            sx={{
              mb: 2,
              height: 45,
              flexGrow: 1,
              backgroundColor:
                theme === "dark"
                  ? "rgba(150,100,255,0.8)" // Purple in dark theme
                  : "rgba(150,100,255,0.9)", // Purple in light theme
              color: "#ffffff",
              textTransform: "none",
              fontSize: 15,
              fontWeight: 600,
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(150,100,255,0.9)"
                    : "rgba(150,100,255,1)",
              },
            }}
          >
            Open Supply Portal
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={() => {
              if (!supplyPortalUrl) return;
              navigator.clipboard.writeText(supplyPortalUrl).then(() => {
                setSnackbarMessage("Supply portal link copied to clipboard");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
              });
            }}
            sx={{
              mb: 2,
              minWidth: 40,
              height: 45, // match height with main button
              backgroundColor:
                theme === "dark"
                  ? "rgba(150,100,255,0.8)" // Purple to match main button in dark theme
                  : "rgba(150,100,255,0.9)", // Purple to match main button in light theme
              color: "#ffffff",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(150,100,255,0.9)"
                    : "rgba(150,100,255,1)",
              },
            }}
          >
            <ContentCopyIcon />
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={() => setVisibleFoldersDialogOpen(true)}
            title="Change Supply Folder Visibility"
            sx={{
              mb: 2,
              minWidth: 40,
              height: 45,
              backgroundColor:
                theme === "dark"
                  ? "rgba(150,100,255,0.8)"
                  : "rgba(150,100,255,0.9)",
              color: "#ffffff",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(150,100,255,0.9)"
                    : "rgba(150,100,255,1)",
              },
            }}
          >
            <SettingsIcon />
          </Button>
        </motion.div>

        {/* Shot List Button */}
        <motion.div variants={itemVariants} style={{ display: "flex", gap: 8 }}>
          <Button
            variant="contained"
            startIcon={<TableRowsTwoToneIcon />}
            fullWidth
            component="a"
            href={clientData?.spreadsheetURL}
            disabled={!clientData?.spreadsheetURL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mb: 2,
              height: 45,
              backgroundColor:
                theme === "dark"
                  ? "rgba(83,162,108,0.9)" // Google Sheets green in dark theme
                  : "rgba(83,162,108,1.0)", // Google Sheets green in light theme
              color: "#ffffff",
              textTransform: "none",
              fontSize: 15,
              fontWeight: 600,
              flexGrow: 1,
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(83,162,108,1.0)"
                    : "rgba(63,142,88,1.0)",
              },
            }}
          >
            Open Shootlist
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!clientData?.spreadsheetURL}
            onClick={() => {
              navigator.clipboard
                .writeText(clientData?.spreadsheetURL)
                .then(() => {
                  setSnackbarMessage("Shootlist copied to clipboard");
                  setSnackbarSeverity("success");
                  setSnackbarOpen(true);
                });
            }}
            sx={{
              mb: 2,
              minWidth: 40,
              height: 45,
              backgroundColor:
                theme === "dark"
                  ? "rgba(83,162,108,0.9)" // Google Sheets green in dark theme
                  : "rgba(83,162,108,1.0)", // Google Sheets green in light theme
              color: "#ffffff",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(83,162,108,1.0)"
                    : "rgba(63,142,88,1.0)",
              },
            }}
          >
            <ContentCopyIcon />
          </Button>
        </motion.div>

        {/* 2. Client Selects Section - CONDITIONAL */}
        {selectsSetup && (
          <motion.div variants={itemVariants} style={{ marginTop: 20 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleClientReselectToggle}
                  disabled={!isAdmin}
                  sx={{
                    height: 45,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 15,
                  }}
                >
                  Allow client to reselect
                </Button>

                <Button
                  fullWidth
                  variant={jobData.selectsAreLocked ? "contained" : "outlined"}
                  startIcon={
                    jobData.selectsAreLocked ? <LockIcon /> : <LockOpenIcon />
                  }
                  onClick={handleSelectsLockedToggle}
                  disabled={!isAdmin}
                  sx={{
                    height: 45,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 15,
                    backgroundColor: jobData.selectsAreLocked
                      ? theme === "dark"
                        ? "rgba(255,100,100,0.8)"
                        : "rgba(255,100,100,0.9)"
                      : undefined,
                    color: jobData.selectsAreLocked ? "white" : undefined,
                    "&:hover": {
                      backgroundColor: jobData.selectsAreLocked
                        ? theme === "dark"
                          ? "rgba(255,100,100,0.9)"
                          : "rgba(255,100,100,1)"
                        : undefined,
                    },
                  }}
                >
                  {jobData.selectsAreLocked ? "Selects Locked" : "Lock Selects"}
                </Button>
              </Box>
            </Box>
          </motion.div>
        )}

        <motion.div variants={itemVariants}>
          <CameraSettings
            jobData={jobData}
            clientData={clientData}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        </motion.div>

        {/* 3. Supply Link Section - CONDITIONAL */}
        {supplyLink && (
          <motion.div variants={itemVariants}>
            <Box sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={handleSupplyLinkClick}
                sx={{
                  height: 48,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500,
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(100,200,100,0.8)"
                      : "rgba(100,200,100,0.9)",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(100,200,100,0.9)"
                        : "rgba(100,200,100,1)",
                  },
                }}
              >
                Supply Link
              </Button>
            </Box>
          </motion.div>
        )}

        {/* 4. Supply Link Menu */}
        <Menu
          anchorEl={supplyLinkMenuAnchor}
          open={Boolean(supplyLinkMenuAnchor)}
          onClose={handleSupplyLinkMenuClose}
        >
          <MenuItem onClick={copySupplyLinkToClipboard}>
            <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />
            Copy To Clipboard
          </MenuItem>
          <Divider />
          <MenuItem onClick={openSupplyLink}>
            <LaunchIcon sx={{ mr: 1 }} fontSize="small" />
            Open Link
          </MenuItem>
        </Menu>

        {/* 5. Notes Filter Toggle Buttons */}
        <motion.div variants={itemVariants}>
          <ToggleButtonGroup
            value={selectedNotesFilter}
            onChange={handleFilterChange}
            sx={{
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 0.8,
              my: 2,
              width: "100%",
            }}
          >
            {isAdmin && (
              <ToggleButton
                value="Pre-Production"
                size="small"
                sx={filterButtonStyles}
              >
                Pre-Production
              </ToggleButton>
            )}
            <ToggleButton
              value="Photographer"
              size="small"
              sx={filterButtonStyles}
            >
              Photographer
            </ToggleButton>
            <ToggleButton
              value="Videographer"
              size="small"
              sx={filterButtonStyles}
            >
              Videographer
            </ToggleButton>
            <ToggleButton value="Operator" size="small" sx={filterButtonStyles}>
              Operator
            </ToggleButton>
            <ToggleButton value="Stylist" size="small" sx={filterButtonStyles}>
              Stylist
            </ToggleButton>
            <ToggleButton
              value="Retoucher"
              size="small"
              sx={filterButtonStyles}
            >
              Retoucher
            </ToggleButton>
          </ToggleButtonGroup>
        </motion.div>

        {/* 8. Notes Container - displays both Job and Client notes */}
        <motion.div variants={itemVariants}>
          <NotesContainer
            jobData={jobData}
            clientData={clientData}
            setHasUnsavedChanges={setHasUnsavedChanges}
            selectedFilters={selectedNotesFilter}
          />
        </motion.div>

        {/* 7. BriefsContainer Component */}
        <motion.div variants={itemVariants}>
          <BriefsContainer
            jobData={jobData}
            clientData={clientData}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        </motion.div>

        {/* 8. Studio Setup Accordions Section */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              mt: 2,
              width: "100%",
              maxWidth: "100%",
              overflowX: "hidden",
            }}
          >
            <Accordion
              defaultExpanded={jobData.studio === "Studio 1"}
              sx={{
                background:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(30, 30, 30, 0.05)",
                mb: 1,
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>Studio 401</Typography>
                  {renderSetupChip(studioCounts.studio401)}
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "hidden",
                  padding: 0,
                }}
              >
                {setups?.studio401 &&
                  setups?.studio401?.map((setup, index) => (
                    <SetupAccordionV2
                      key={setup.id || index}
                      setup={setup}
                      defaultExpanded={index === 0}
                      setups={setups}
                      setSetups={setSetups}
                      clientData={clientData}
                    />
                  ))}
              </AccordionDetails>
              <AccordionActions>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => handleCreateSetup("studio401")}
                  >
                    New Setup
                  </Button>
              </AccordionActions>
            </Accordion>

            <Accordion
              defaultExpanded={jobData.studio === "Studio 4"}
              sx={{
                background:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(30, 30, 30, 0.05)",
                mb: 1,
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>Studio 301</Typography>
                  {renderSetupChip(studioCounts.studio301)}
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "hidden",
                  padding: 0,
                }}
              >
                {setups?.studio301 &&
                  setups?.studio301?.map((setup, index) => (
                    <SetupAccordionV2
                      key={setup.id || index}
                      setup={setup}
                      defaultExpanded={index === 0}
                      setups={setups}
                      setSetups={setSetups}
                      clientData={clientData}
                    />
                  ))}
              </AccordionDetails>
              <AccordionActions>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => handleCreateSetup("studio301")}
                  >
                    New Setup
                  </Button>
              </AccordionActions>
            </Accordion>

            <Accordion
              defaultExpanded={jobData.studio === "Studio 3"}
              sx={{
                background:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(30, 30, 30, 0.05)",
                mb: 1,
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>Studio 302</Typography>
                  {renderSetupChip(studioCounts.studio302)}
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "hidden",
                  padding: 0,
                }}
              >
                {setups?.studio302 &&
                  setups?.studio302?.map((setup, index) => (
                    <SetupAccordionV2
                      key={setup.id || index}
                      setup={setup}
                      defaultExpanded={index === 0}
                      setups={setups}
                      setSetups={setSetups}
                      clientData={clientData}
                    />
                  ))}
              </AccordionDetails>
              <AccordionActions>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => handleCreateSetup("studio302")}
                  >
                    New Setup
                  </Button>
              </AccordionActions>
            </Accordion>

            <Accordion
              defaultExpanded={
                jobData.studio === "Studio 2A" ||
                jobData.studio === "Studio 2B" ||
                jobData.studio === "Studio 2C"
              }
              sx={{
                background:
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(30, 30, 30, 0.05)",
                mb: 1,
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>Studio 403 (GHOST ROOM)</Typography>
                  {renderSetupChip(studioCounts.studio403)}
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "hidden",
                  padding: 0,
                }}
              >
                {setups?.studio403 &&
                  setups?.studio403?.map((setup, index) => (
                    <SetupAccordionV2
                      key={setup.id || index}
                      setup={setup}
                      defaultExpanded={index === 0}
                      setups={setups}
                      setSetups={setSetups}
                      clientData={clientData}
                    />
                  ))}
              </AccordionDetails>
              <AccordionActions>
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => handleCreateSetup("studio403")}
                  >
                    New Setup
                  </Button>
              </AccordionActions>
            </Accordion>
          </Box>
        </motion.div>

        {/* 9. Important Dates */}
        <motion.div variants={itemVariants}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="en-au"
          >
            <Grid
              container
              spacing={2}
              sx={{
                m: 0,
                width: "100%",
                maxWidth: "100%",
                display: "flex",
                flexWrap: "nowrap",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(35,35,35,0.7)"
                      : "rgba(235,235,235,0.9)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                      : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                  padding: 1.5,
                  marginTop: 2,
                  width: "100%",
                }}
              >
                <MobileDatePicker
                  label="Shoot Date"
                  fullWidth
                  format="ddd D/M/YYYY"
                  value={
                    jobData.bookedStart ? dayjs(jobData.bookedStart) : null
                  }
                  disabled
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "filled",
                      InputProps: { disableUnderline: true },
                      sx: {
                        "& .MuiInputLabel-root": {
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.7)"
                              : "rgba(0,0,0,0.7)",
                          fontWeight: 500,
                          fontSize: "0.95rem",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&.MuiInputLabel-shrink": {
                            transform: "translate(14px, 7px) scale(0.75)",
                          },
                        },
                        "& .MuiFilledInput-root": {
                          borderRadius: "8px",
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(45,45,45,0.7)"
                              : "rgba(250,250,250,0.9)",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(55,55,55,0.8)"
                                : "rgba(255,255,255,1)",
                          },
                          "&.Mui-focused": {
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(60,60,60,0.9)"
                                : "rgba(255,255,255,1)",
                            boxShadow:
                              theme === "dark"
                                ? "0 0 0 2px rgba(100,100,255,0.5)"
                                : "0 0 0 2px rgba(100,100,255,0.3)",
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(35,35,35,0.7)"
                      : "rgba(235,235,235,0.9)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                      : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                  padding: 1.5,
                  marginTop: 2,
                  width: "100%",
                }}
              >
                <MobileDatePicker
                  label="Due Date"
                  fullWidth
                  format="ddd D/M/YYYY"
                  value={dueDate}
                  onChange={handleDueDateChange}
                  disabled={!isAdmin}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "filled",
                      InputProps: { disableUnderline: true },
                      sx: {
                        "& .MuiInputLabel-root": {
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.7)"
                              : "rgba(0,0,0,0.7)",
                          fontWeight: 500,
                          fontSize: "0.95rem",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&.MuiInputLabel-shrink": {
                            transform: "translate(14px, 7px) scale(0.75)",
                          },
                        },
                        "& .MuiFilledInput-root": {
                          borderRadius: "8px",
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(45,45,45,0.7)"
                              : "rgba(250,250,250,0.9)",
                          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(55,55,55,0.8)"
                                : "rgba(255,255,255,1)",
                          },
                          "&.Mui-focused": {
                            backgroundColor:
                              theme === "dark"
                                ? "rgba(60,60,60,0.9)"
                                : "rgba(255,255,255,1)",
                            boxShadow:
                              theme === "dark"
                                ? "0 0 0 2px rgba(100,100,255,0.5)"
                                : "0 0 0 2px rgba(100,100,255,0.3)",
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Grid>

            <Box
              sx={{
                borderRadius: "10px",
                overflow: "hidden",
                backgroundColor:
                  theme === "dark"
                    ? "rgba(35,35,35,0.7)"
                    : "rgba(235,235,235,0.9)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow:
                  theme === "dark"
                    ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                    : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                padding: 1.5,
                marginTop: 2,
                position: "relative",
                width: "100%",
              }}
            >
              <MobileDatePicker
                label="Express Due Date"
                format="ddd D/M/YYYY"
                value={expressDueDate}
                onChange={handleExpressDueDateChange}
                disabled={!isAdmin}
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "filled",
                    InputProps: {
                      disableUnderline: true,
                      endAdornment: expressDueDate && isAdmin && (
                        <IconButton
                          size="small"
                          onClick={clearExpressDate}
                          sx={{
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.7)"
                                : "rgba(0,0,0,0.7)",
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      ),
                    },
                    sx: {
                      "& .MuiInputLabel-root": {
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.7)"
                            : "rgba(0,0,0,0.7)",
                        fontWeight: 500,
                        fontSize: "0.95rem",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&.MuiInputLabel-shrink": {
                          transform: "translate(14px, 7px) scale(0.75)",
                        },
                      },
                      "& .MuiFilledInput-root": {
                        borderRadius: "8px",
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(45,45,45,0.7)"
                            : "rgba(250,250,250,0.9)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(55,55,55,0.8)"
                              : "rgba(255,255,255,1)",
                        },
                        "&.Mui-focused": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(60,60,60,0.9)"
                              : "rgba(255,255,255,1)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0 0 2px rgba(100,100,255,0.5)"
                              : "0 0 0 2px rgba(100,100,255,0.3)",
                        },
                      },
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </motion.div>

        {/* 10. Staff Assignments */}
        <motion.div variants={itemVariants}>
          <Grid
            container
            spacing={2}
            sx={{
              mt: 2,
              mx: 0,
              width: "100%",
              maxWidth: "100%",
              display: "flex",
              gap: 2,
            }}
          >
            <UniversalTextField
              collectionName="JobData"
              docId={jobData.id}
              fieldName="photographer"
              value={findStaffName(jobData.photographer) || "Not assigned"}
              label={
                jobData?.jobTypeFull?.includes("-V")
                  ? "Videographer"
                  : "Photographer"
              }
              disabled
              adminOnly={false}
              sx={{ marginTop: 0, width: "48%" }}
            />

            {jobData.additionalPhotographers &&
              jobData.additionalPhotographers.length > 0 &&
              jobData.additionalPhotographers.map((staffId, index) => (
                <UniversalTextField
                  collectionName="JobData"
                  docId={jobData.id}
                  fieldName={`additionalPhotographers[${index}]`}
                  value={findStaffName(staffId) || "Not assigned"}
                  label={`Additional ${jobData?.jobTypeFull?.includes("-V")
                    ? "Videographer"
                    : "Photographer"
                    } ${index + 1}`}
                  disabled
                  adminOnly={false}
                  sx={{ marginTop: 0, width: "48%" }}
                />
              ))}

            <UniversalTextField
              collectionName="JobData"
              docId={jobData.id}
              fieldName="operator"
              value={findStaffName(jobData.operator) || "Not assigned"}
              label="Operator"
              disabled
              adminOnly={false}
              sx={{ marginTop: 0, width: "48%" }}
            />

            {jobData.additionalOperators &&
              jobData.additionalOperators.length > 0 &&
              jobData.additionalOperators.map((staffId, index) => (
                <UniversalTextField
                  collectionName="JobData"
                  docId={jobData.id}
                  fieldName={`additionalOperators[${index}]`}
                  value={findStaffName(staffId) || "Not assigned"}
                  label={`Additional Operator ${index + 1}`}
                  disabled
                  adminOnly={false}
                  sx={{ marginTop: 0, width: "48%" }}
                />
              ))}

            <UniversalTextField
              collectionName="JobData"
              docId={jobData.id}
              fieldName="stylist"
              value={findStaffName(jobData.stylist) || "Not assigned"}
              label="Stylist"
              disabled
              adminOnly={false}
              sx={{ marginTop: 0, width: "48%" }}
            />

            {jobData.additionalStylists &&
              jobData.additionalStylists.length > 0 &&
              jobData.additionalStylists.map((staffId, index) => (
                <UniversalTextField
                  collectionName="JobData"
                  docId={jobData.id}
                  fieldName={`additionalStylists[${index}]`}
                  value={findStaffName(staffId) || "Not assigned"}
                  label={`Additional Stylist ${index + 1}`}
                  disabled
                  adminOnly={false}
                  sx={{ marginTop: 0, width: "48%" }}
                />
              ))}

            <UniversalTextField
              collectionName="JobData"
              docId={jobData.id}
              fieldName="imagesScheduledToBeShot"
              value={jobData.imagesScheduledToBeShot || ""}
              label={
                jobData?.jobTypeFull?.includes("-V")
                  ? "Videos Scheduled to be Shot"
                  : "Images Scheduled to be Shot"
              }
              disabled
              adminOnly={false}
              multiline
              rows={2}
              sx={{ marginTop: 0 }}
            />
          </Grid>
        </motion.div>

        {/* Attachments Section */}
        <motion.div variants={itemVariants}>
          <AttachmentsV2
            jobData={jobData}
            clientData={clientData}
            setUrlModalUrl={setUrlModalUrl}
            setURLModalOpen={setURLModalOpen}
          />
        </motion.div>

        {/* Snackbar for clipboard notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </motion.div>

      {/* Supplied Folders Dialog */}
      <SuppliedFoldersDialog
        open={visibleFoldersDialogOpen}
        onClose={() => setVisibleFoldersDialogOpen(false)}
        jobData={jobData}
      />
    </Box>
  );
};

export default ShootInfoTab;
